// Atelier de démonstration : données fictives, recalées sur la date du jour.
import { DEFAULT_HOURS, DEFAULT_PERMISSIONS, type CatalogItem, type Incident, type Invitation, type Job, type Machine, type Member, type Operator, type Organization, type Task, type TaskEvent, type TaskWithJob, type DocPage, type JobDocument } from "./types.ts";
import { DEMO_BAT_PAGES } from "./demoBat.ts";
import { addWorkingMinutes, nextWorkingInstant, startOfDay } from "../lib/time.ts";
import { optimize } from "../lib/optimizer.ts";

export interface DemoDb {
  org: Organization;
  machines: Machine[];
  operators: Operator[];
  pins: Record<string, string>;
  jobs: Job[];
  tasks: Task[];
  events: TaskEvent[];
  incidents: Incident[];
  devices: { id: string; org_id: string; machine_id: string; name: string; token: string; created_at: string; last_seen_at: string | null }[];
  pairing: { code: string; machine_id: string; expires_at: string }[];
  members: Member[];
  invitations: Invitation[];
  catalog: CatalogItem[];
  /** Documents des jobs, pages comprises (gardés dans le navigateur). */
  documents?: (JobDocument & { pageData: DocPage[] })[];
}

let seq = 0;
const id = (p: string) => `${p}-${(++seq).toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export function makeDemo(now = new Date()): DemoDb {
  const org: Organization = {
    id: "demo-org", name: "Imprimerie de démonstration", siret: null, timezone: "Europe/Paris",
    hours: DEFAULT_HOURS, permissions: { ...DEFAULT_PERMISSIONS }, created_at: now.toISOString(),
  };
  const m = (name: string, over: Partial<Machine>): Machine => ({
    id: id("m"), org_id: org.id, name, type: "plieuse", operations: ["pli"], formats: [], speed_per_hour: 8000,
    setup_min: 25, position: 0, active: true, specs: {}, ...over,
  });
  const machines = [
    m("Plieuse 1 · Stahl", { position: 0, speed_per_hour: 9000, setup_min: 25, specs: { poches: 6, couteaux: 3, grammage_min: 60, grammage_max: 250, format_max: "52 × 74 cm" } }),
    m("Plieuse 2 · Horizon", { position: 1, speed_per_hour: 7000, setup_min: 20, formats: ["A3", "A4", "A5", "DL", "21x21"], specs: { poches: 4, couteaux: 2, grammage_max: 200 } }),
    m("Encarteuse-piqueuse", { position: 2, type: "encarteuse_piqueuse", operations: ["encartage_piquage", "encartage", "piquage"], speed_per_hour: 4500, setup_min: 40,
      specs: { margeurs: 6, tetes: 2, agrafes: ["2 points", "4 points", "Boucles (oméga)"], trilaterale: true, stacker: true, pages_max: 96 } }),
    m("Dos carré collé · BQ", { position: 3, type: "dos_carre_colle", operations: ["dos_carre_colle"], speed_per_hour: 1800, setup_min: 45,
      specs: { stations: 12, colles: ["EVA (hotmelt)", "PUR"], dos_max: 40, trilaterale: true } }),
    m("Pelliculeuse", { position: 4, type: "pelliculeuse", operations: ["pelliculage"], speed_per_hour: 3000, setup_min: 30,
      specs: { films: ["Brillant", "Mat", "Soft touch"], laize_max: 720, recto_verso: false } }),
  ];
  const operators: Operator[] = [
    { id: id("o"), org_id: org.id, name: "Marc", role: "conducteur", machine_ids: [machines[0].id, machines[1].id], active: true },
    { id: id("o"), org_id: org.id, name: "Sophie", role: "chef_atelier", machine_ids: [], active: true },
    { id: id("o"), org_id: org.id, name: "Karim", role: "conducteur", machine_ids: [machines[2].id, machines[3].id], active: true },
    { id: id("o"), org_id: org.id, name: "Léa", role: "conducteur", machine_ids: [machines[4].id, machines[1].id], active: true },
  ];
  const pins = { [operators[0].id]: "1234", [operators[1].id]: "2345", [operators[2].id]: "3456", [operators[3].id]: "4567" };

  const day0 = startOfDay(now);
  const at = (days: number, h: number, min = 0) => { const d = new Date(day0); d.setDate(d.getDate() + days); d.setHours(h, min, 0, 0); return d.toISOString(); };
  const jobs: Job[] = [];
  const tasks: Task[] = [];
  const mk = (
    of: string, client: string, title: string, qty: number, dueDays: number, ops: Partial<Task>[],
    extra: Partial<Job> = {},
  ) => {
    const job: Job = {
      id: id("j"), org_id: org.id, of_number: of, client, title, quantity: qty, due_at: at(dueDays, 16),
      priority: "normal", sheets_ready_at: null, notes: "", status: "a_planifier",
      created_at: new Date(new Date(at(-3, 9)).getTime() + jobs.length * 60_000).toISOString(), updated_at: now.toISOString(), ...extra,
    };
    jobs.push(job);
    ops.forEach((o, i) => tasks.push({
      id: id("t"), org_id: org.id, job_id: job.id, seq: i + 1, operation: "pli", fold_type: "simple",
      format_in: "A3", format_out: "A4", paper: "Couché mat", grammage: 135, pages: null, machine_id: null,
      planned_start: null, planned_end: null, setup_min: null, run_min: null, locked: false,
      pub_machine_id: null, pub_start: null, status: "a_planifier", produced_qty: 0, waste_qty: 0,
      started_at: null, finished_at: null, updated_at: now.toISOString(), params: {}, packaging: null, operator_id: null, ...o,
    }));
    return job;
  };
  const A3A4 = { format_in: "A3", format_out: "A4", fold_type: "simple" };
  const A4DL = { format_in: "A4", format_out: "DL", fold_type: "roulé" };
  const A3A5 = { format_in: "A3", format_out: "A5", fold_type: "croisé" };
  const ENC = { operation: "encartage_piquage" as const, format_in: "A4", format_out: "A4", fold_type: null, pages: 16,
    params: { agrafes: "2 points", trilaterale: true }, packaging: "Colisage par 100" };
  const PELLIC = { operation: "pelliculage" as const, format_in: "SRA3", format_out: "SRA3", fold_type: null, paper: "Couché mat", grammage: 250,
    params: { film: "Soft touch", faces: "Recto" }, packaging: "Pile sur palette" };

  const j2611 = mk("OF-2611", "Office de tourisme", "Dépliant été · 3 volets", 36000, 1, [A4DL], {
    notes: "Sens d'ouverture vers la droite. Contrôle du pli tous les 500 ex.",
  });
  mk("OF-2612", "Agence Horizon", "Flyer salon A3 → A4", 24000, 1, [A3A4]);
  mk("OF-2613", "Mairie", "Bulletin municipal 16 p.", 4000, 2, [{ ...A3A4, paper: "Offset", grammage: 90 }, ENC], {
    notes: "Agrafes 2 points. Colisage par 100, étiquette sur chaque carton.",
  });
  mk("OF-2614", "Cave du Littoral", "Carte des vins", 9000, 2, [A3A5]);
  mk("OF-2615", "Clinique des Dunes", "Livret d'accueil 16 p.", 3000, 3, [{ ...A3A4, paper: "Couché brillant", grammage: 115 }, ENC]);
  mk("OF-2616", "Agence Horizon", "Dépliant produit", 18000, 3, [A4DL]);
  mk("OF-2617", "Camping des Pins", "Plan du site", 15000, 3, [A3A5]);
  mk("OF-2618", "Médiathèque", "Programme trimestriel", 21000, 4, [A3A4]);
  mk("OF-2619", "Club de voile", "Calendrier des régates", 10000, 4, [A4DL]);
  mk("OF-2620", "Office de tourisme", "Guide hébergement 16 p.", 5000, 4, [{ ...A3A4 }, ENC]);
  mk("OF-2621", "Boulangerie Lemaire", "Carte fidélité pliée", 12000, 5, [A3A5], { sheets_ready_at: at(1, 14) });
  mk("OF-2625", "Musée de la Marine", "Catalogue d'exposition 16 p.", 6000, 6, [{ ...A3A4, paper: "Couché mat", grammage: 115 }, ENC], {
    sheets_ready_at: at(1, 9), notes: "Couverture fournie à part, encarter en dernier.",
  });
  mk("OF-2626", "Domaine des Dunes", "Plaquette vins · couverture soft touch", 4000, 4,
    [PELLIC, { format_in: "SRA3", format_out: "A4", fold_type: "simple", paper: "Couché mat", grammage: 250, params: { plis: 1 }, packaging: "Paquets de 50 sous film" }]);
  mk("OF-2627", "Office de tourisme", "Guide découverte 64 p.", 2500, 5,
    [{ operation: "dos_carre_colle", format_in: "A4", format_out: "A5", fold_type: null, paper: "Offset", grammage: 90, pages: 64,
      params: { colle: "PUR", dos_mm: 5, rainures: "4", trilaterale: true }, packaging: "Colisage par 20" }], { sheets_ready_at: at(1, 13) });
  // arrivés ce matin, pas encore planifiés
  const late1 = mk("OF-2622", "Mairie", "Invitation cérémonie", 6000, 2, [A3A5], { priority: "urgent" });
  const late2 = mk("OF-2623", "Agence Horizon", "Flyer promo", 27000, 5, [A3A4]);
  const late3 = mk("OF-2624", "École de musique", "Programme concert", 8000, 6, [A4DL]);

  const members: Member[] = [
    { user_id: "demo", org_id: org.id, role: "admin", email: "demo@ordopli.fr", name: "Vous (démonstration)" },
    { user_id: "demo-2", org_id: org.id, role: "production", email: "claire@imprimerie-demo.fr", name: "Claire Martin" },
    { user_id: "demo-3", org_id: org.id, role: "chef", email: "sophie@imprimerie-demo.fr", name: "Sophie Leroy" },
    { user_id: "demo-4", org_id: org.id, role: "saisie", email: "accueil@imprimerie-demo.fr", name: "Accueil" },
  ];
  let pos = 0;
  const cat = (kind: CatalogItem["kind"], scope: string, label: string): CatalogItem =>
    ({ id: id("c"), org_id: org.id, kind, scope, machine_id: null, label, position: pos++ });
  const catalog: CatalogItem[] = [
    ...["En vrac en carton", "Paquets de 100 sous film", "Paquets de 50 sous film", "Sur palette filmée"].map((l) => cat("conditionnement", "plieuse", l)),
    ...["Colisage par 50", "Colisage par 100", "Banderolé par 25", "Mise sous film individuelle"].map((l) => cat("conditionnement", "encarteuse_piqueuse", l)),
    ...["Colisage par 20", "Mise sous film individuelle"].map((l) => cat("conditionnement", "dos_carre_colle", l)),
    ...["Pile sur palette", "Intercalaires tous les 500"].map((l) => cat("conditionnement", "pelliculeuse", l)),
  ];
  const documents: DemoDb["documents"] = [{
    id: id("d"), job_id: j2611.id, name: "BAT dépliant été.pdf", kind: "bat", pages: DEMO_BAT_PAGES.length,
    thumb: DEMO_BAT_PAGES[0].data, created_at: now.toISOString(), pageData: DEMO_BAT_PAGES,
  }];
  const db: DemoDb = { org, machines, operators, pins, jobs, tasks, events: [], incidents: [], devices: [], pairing: [], members, invitations: [], catalog, documents };

  // Ce qui est déjà fait ou en cours aujourd'hui
  const start = nextWorkingInstant(new Date(day0.getTime() + 8 * 3600_000), org.hours);
  const twj = (t: Task): TaskWithJob => ({ ...t, job: jobs.find((j) => j.id === t.job_id)! });
  const unplanned = new Set([late1.id, late2.id, late3.id]);
  const plan = optimize({
    machines, tasks: tasks.filter((t) => !unplanned.has(t.job_id)).map(twj), hours: org.hours,
    now: now < start ? now : start,
  });
  for (const s of plan.slots) {
    const t = tasks.find((x) => x.id === s.task_id)!;
    Object.assign(t, {
      machine_id: s.machine_id, planned_start: s.start.toISOString(), planned_end: s.end.toISOString(),
      setup_min: s.setup_min, run_min: s.run_min, status: "planifie",
      pub_machine_id: s.machine_id, pub_start: s.start.toISOString(),
    });
  }
  // la première étape de chaque machine qui a déjà commencé avant maintenant : terminée ou en cours
  for (const mach of machines) {
    const list = tasks.filter((t) => t.machine_id === mach.id && t.planned_start).sort((a, b) => a.planned_start!.localeCompare(b.planned_start!));
    for (const t of list) {
      const s = new Date(t.planned_start!), e = new Date(t.planned_end!);
      if (e <= now) {
        Object.assign(t, { status: "termine", started_at: t.planned_start, finished_at: t.planned_end, produced_qty: jobs.find((j) => j.id === t.job_id)!.quantity, waste_qty: 40 });
        db.events.push(ev(t, "calage", s), ev(t, "production", addWorkingMinutes(s, t.setup_min || 0, org.hours)), ev(t, "termine", e));
      } else if (s <= now) {
        const done = Math.min(0.9, (now.getTime() - s.getTime()) / Math.max(1, e.getTime() - s.getTime()));
        const job = jobs.find((j) => j.id === t.job_id)!;
        Object.assign(t, { status: "production", started_at: t.planned_start, produced_qty: Math.round(job.quantity * done / 100) * 100 });
        db.events.push(ev(t, "calage", s), ev(t, "production", addWorkingMinutes(s, t.setup_min || 0, org.hours)));
      }
    }
  }
  // un bourrage terminé ce matin sur la plieuse 2, pour le journal
  const p2 = tasks.find((t) => t.machine_id === machines[1].id && t.status !== "a_planifier");
  if (p2 && now.getTime() - start.getTime() > 3600_000) {
    db.incidents.push({
      id: id("i"), org_id: org.id, machine_id: machines[1].id, task_id: p2.id, operator_id: operators[0].id,
      kind: "Bourrage", comment: "Feuilles collées", started_at: new Date(start.getTime() + 50 * 60_000).toISOString(),
      ended_at: new Date(start.getTime() + 68 * 60_000).toISOString(),
    });
  }
  for (const j of jobs) j.status = jobStatus(tasks.filter((t) => t.job_id === j.id), j.status);
  return db;

  function ev(t: Task, kind: TaskEvent["kind"], when: Date): TaskEvent {
    return { id: id("e"), org_id: org.id, task_id: t.id, machine_id: t.machine_id!, operator_id: operators[0].id, kind, qty: null, at: when.toISOString() };
  }
}

/** Même règle que le déclencheur SQL refresh_job_status. */
export function jobStatus(tasks: Task[], current: Job["status"]): Job["status"] {
  if (current === "annule") return current;
  if (!tasks.length) return "a_planifier";
  if (tasks.every((t) => t.status === "termine")) return "termine";
  if (tasks.some((t) => ["calage", "production", "pause", "termine"].includes(t.status))) return "en_cours";
  if (tasks.some((t) => t.status === "planifie")) return "planifie";
  return "a_planifier";
}
